/**
 * AgreementDocumentsSection — every agreement on the Documents page, in two headings.
 *
 * A card lists what is being SIGNED IN THE APP: who has signed, who has not, and what this
 * driver is expected to do about it. Every card therefore carries exactly one action, chosen
 * by the state the driver is actually in:
 *
 *   waiting for me      Review & Sign
 *   executed            View agreement          (the at-rest reader, `/agreement/:id`)
 *   witness signed      View agreement          (a witness agreement, once its witness has)
 *   signed, not done    View document           (back into the driver's own ceremony)
 *   withdrawn / other   nothing, and a sentence saying so (`inactionReason`, in `esign.ts`
 *                       with the predicate it is the other half of)
 *
 * ── THE EXECUTED CARD USED TO SAVE A FILE ─────────────────────────────────────
 * It said "Download your copy" and it did exactly that. The owner ruled on 2026-08-21 that a
 * signed instrument is shown and not handed over, so the action now opens the at-rest reader.
 * One action still, in the same place, doing something different — and the caption under it
 * names the standing Terms the Schedule incorporates, which is the second thing a driver
 * would otherwise have needed a second action for.
 *
 * ── AND THE WITNESS AGREEMENT IS GATED ON ITS WITNESS ─────────────────────────
 * The witness agreement freezes when the witness's link is ISSUED, which is before they sign
 * it. So there is a window in which its text exists, the driver's own row policy admits it,
 * and nobody has witnessed anything. `agreementViewable` is what keeps a card from offering
 * that window; fleet-backend refuses it server-side in the PR that follows this one.
 *
 * ── TWO HEADINGS, BECAUSE ONE OF THEM IS A REQUEST ───────────────────────────
 * The list is the SAME `get_agreement_documents` the admin board reads, and it holds a
 * contract's whole signing history. Printing all of it under "Agreements to sign" put a
 * finished deposit agreement and a WITHDRAWN insurance loan under a heading that asks the
 * driver to do something about them — an ask they cannot satisfy and should not try to.
 * So that heading covers only documents that can still move, and everything settled moves
 * below it, where a driver who has signed can still find what they signed.
 *
 * ── AND ONE SIGNED SECTION, BECAUSE THERE IS ONLY ONE RECORD ─────────────────
 * This section used to be followed by two more — "Agreements" and "Deposit Agreements",
 * built from the insights payload's legacy rows — and a hire purchase agreement signed in
 * the app appeared under BOTH: an in-app card offering "Download your copy" and, further
 * down and in a different style, a legacy card offering "View Agreement" on the very same
 * PDF, which `agreement_finalize` had written back onto the legacy row.
 *
 * So the legacy rows come in here instead (`unifyAgreementDocuments`) and are deduplicated
 * against the documents BY ARC. What survives that is a genuine second thing: a record
 * signed on paper, for which no in-app document was ever raised — the state the admin board
 * badges "Paper", and it is badged, titled and named the same way here.
 */

import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Eye, ExternalLink, FileSignature, PenLine, Printer } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { openSignedUrl } from '@/lib/openSignedUrl';
import { termsCardCaption } from '@/lib/agreement-terms-authority';
import {
    DOC_TYPE_LABELS,
    documentLabel,
    agreementViewable,
    awaitingMySignature,
    inactionReason,
    isMyParty,
    unifyAgreementDocuments,
    type LegacyContractAgreement,
    type LegacyDepositAgreement,
    type PaperAgreement,
} from '@/lib/esign';
import {
    useAgreementDocuments,
    type AgreementDocumentSummary,
} from '@/hooks/useAgreements';
import SignatoryChips from '@/components/agreements/SignatoryChips';

interface Props {
    contractId: number | null | undefined;
    /** `contract_agreements` as `get_contract_insights` returns them. */
    contractAgreements?: LegacyContractAgreement[] | null;
    /** `contract_deposit_agreements` joined to their deposits, same payload. */
    depositAgreements?: LegacyDepositAgreement[] | null;
}

const currency = new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' });

function statusBadge(document: AgreementDocumentSummary) {
    switch (document.status) {
        case 'COMPLETED':
            return { label: document.finalized_at ? 'Complete' : 'Signed', variant: 'default' as const };
        case 'IN_SIGNING':
            return { label: 'In signing', variant: 'secondary' as const };
        case 'PENDING_REVIEW':
            return { label: 'Being prepared', variant: 'outline' as const };
        case 'VOIDED':
            return { label: 'Withdrawn', variant: 'destructive' as const };
        case 'SUPERSEDED':
            return { label: 'Replaced', variant: 'outline' as const };
        default:
            return { label: document.status, variant: 'outline' as const };
    }
}

export default function AgreementDocumentsSection({
    contractId,
    contractAgreements,
    depositAgreements,
}: Props) {
    const navigate = useNavigate();
    const { data, isLoading, error } = useAgreementDocuments(contractId);

    if (isLoading) {
        return (
            <div>
                <h3 className="mb-4 text-lg font-semibold">Agreements</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Skeleton className="h-36 w-full" />
                    <Skeleton className="h-36 w-full" />
                </div>
            </div>
        );
    }

    // A DRIVER ON AN ENVIRONMENT WITHOUT THE SUBSYSTEM STILL HAS THEIR PAPERWORK. The read
    // failing means there are no in-app documents to show, and nothing more: the scans come
    // from the insights payload, which is the same one that drew this page before any of this
    // existed. Passing `[]` is what makes every legacy row fall through as unmatched.
    const documents = error || !data ? [] : data;

    const { live, signed } = unifyAgreementDocuments({
        documents,
        contractAgreements,
        depositAgreements,
    });

    if (live.length === 0 && signed.length === 0) return null;

    const renderCard = (document: AgreementDocumentSummary) => {
        const badge = statusBadge(document);
        const mine = (document.signatures ?? []).find((row) => isMyParty(row.party)) ?? null;
        // The same predicate the section badge counts with — see `esign.ts`.
        const awaitingMe = awaitingMySignature(document);
        // Whether the at-rest reader may be opened at all: finalized, or a witness agreement
        // whose witness has signed. Both halves live in `esign.ts` beside the predicate the
        // inaction sentence is chosen by, because the two decide the card between them.
        const viewable = agreementViewable(document);
        // Present only once fleet-backend projects the cited booklet; absent until then.
        const caption = termsCardCaption(document.terms_form_code, document.terms_version);

        return (
            <Card key={document.id} className="overflow-hidden">
                <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex min-w-0 items-start gap-3">
                            <div className="rounded-lg bg-muted p-2 text-foreground">
                                <FileSignature className="h-5 w-5" />
                            </div>
                            <div className="min-w-0">
                                <p className="truncate text-sm font-semibold">
                                    {documentLabel(document.title, document.doc_type)}
                                </p>
                                <p className="mt-0.5 text-xs tabular-nums text-muted-foreground">
                                    {document.reference_no}
                                    {document.completed_at
                                        ? ` · completed ${format(new Date(document.completed_at), 'MMM d, yyyy')}`
                                        : ''}
                                </p>
                            </div>
                        </div>
                        <Badge variant={badge.variant} className="shrink-0">
                            {badge.label}
                        </Badge>
                    </div>
                </CardHeader>

                <CardContent className="space-y-3 pt-0">
                    <SignatoryChips signatures={document.signatures} />

                    {awaitingMe && (
                        <Button
                            className="w-full"
                            onClick={() => navigate(`/sign/${document.id}`)}
                        >
                            <PenLine className="h-4 w-4" />
                            Review &amp; Sign
                        </Button>
                    )}

                    {!awaitingMe && viewable && (
                        <div className="space-y-1.5">
                            <Button
                                variant="outline"
                                className="w-full"
                                onClick={() => navigate(`/agreement/${document.id}`)}
                            >
                                <Eye className="h-4 w-4" />
                                View agreement
                            </Button>
                            {caption && (
                                <p className="text-center text-[11px] text-muted-foreground">
                                    {caption}
                                </p>
                            )}
                        </div>
                    )}

                    {!awaitingMe && !viewable && mine?.status === 'SIGNED' && (
                        <Button
                            variant="outline"
                            className="w-full"
                            onClick={() => navigate(`/sign/${document.id}`)}
                        >
                            <Eye className="h-4 w-4" />
                            View document
                        </Button>
                    )}

                    {!awaitingMe && !viewable && mine?.status !== 'SIGNED' && (
                        <p className="text-[11.5px] text-muted-foreground">
                            {inactionReason(document)}
                        </p>
                    )}
                </CardContent>
            </Card>
        );
    };

    /**
     * The same card, for a record that came back on paper.
     *
     * IT IS DELIBERATELY THE SAME CARD FAMILY and not the old legacy one: to a driver these
     * are the same kind of object — an agreement they signed and may want to look at — and
     * the only honest difference is HOW it was signed, which is what the tag says. The tag,
     * the wording and the title all match the admin board's own paper row, so a driver and an
     * admin looking at the same agreement are looking at the same name for it.
     */
    const renderPaperCard = (paper: PaperAgreement) => (
        <Card key={paper.key} className="overflow-hidden">
            <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-start gap-3">
                        <div className="rounded-lg bg-muted p-2 text-foreground">
                            <FileSignature className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                            <p className="truncate text-sm font-semibold">
                                {DOC_TYPE_LABELS[paper.docType] ?? paper.docType}
                            </p>
                            <p className="mt-0.5 text-xs tabular-nums text-muted-foreground">
                                {paper.depositAmount !== null &&
                                    `${currency.format(paper.depositAmount)} · `}
                                {paper.depositDate &&
                                    `paid ${format(new Date(paper.depositDate), 'MMM d, yyyy')} · `}
                                signed {format(new Date(paper.signedAt), 'MMM d, yyyy')}
                            </p>
                        </div>
                    </div>
                    <Badge variant="outline" className="shrink-0 gap-1 font-normal">
                        <Printer className="h-3 w-3" />
                        Paper
                    </Badge>
                </div>
            </CardHeader>

            <CardContent className="space-y-3 pt-0">
                {paper.url ? (
                    <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => openSignedUrl(paper.url)}
                    >
                        <ExternalLink className="h-4 w-4" />
                        View scan
                    </Button>
                ) : (
                    <p className="text-[11.5px] text-muted-foreground">
                        Signed on paper. Primelane holds the original.
                    </p>
                )}
            </CardContent>
        </Card>
    );

    return (
        <div className="space-y-8">
            {live.length > 0 && (
                <div>
                    <h3 className="mb-4 text-lg font-semibold">Agreements to sign</h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        {live.map(renderCard)}
                    </div>
                </div>
            )}

            {signed.length > 0 && (
                <div>
                    <h3 className="mb-4 text-lg font-semibold">Signed agreements</h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        {signed.map((entry) =>
                            entry.kind === 'document'
                                ? renderCard(entry.document)
                                : renderPaperCard(entry.paper),
                        )}
                    </div>
                    <p className="mt-4 text-[11.5px] leading-relaxed text-muted-foreground">
                        Signed agreements stay here — you can read your copy whenever you need it.
                        Ask Primelane if you need one sent to you.
                    </p>
                </div>
            )}

            {/* The anti-phishing line is about the SIGNING surface — it means nothing on a page
                whose only agreements were signed on paper, and a warning shown where it does not
                apply is one a driver learns to skip. */}
            {documents.length > 0 && (
                <p className="text-[11.5px] leading-relaxed text-muted-foreground">
                    Primelane never asks for your PIN or password inside a document.
                </p>
            )}
        </div>
    );
}
